import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../Styles/plans.css';

// Tooltip Component
const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const showTooltip = (e) => {
    const rect = e.target.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    let top, left;

    switch (position) {
      case 'top':
        top = rect.top - (tooltipRect?.height || 0) - 8;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - (tooltipRect?.width || 0) - 8;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + 8;
        break;
      default:
        top = rect.top - (tooltipRect?.height || 0) - 8;
        left = rect.left + rect.width / 2;
    }

    setTooltipStyle({ top, left, transform: 'translateX(-50%)' });
    setIsVisible(true);
  };

  return (
    <div
      className="tooltip-container"
      onMouseEnter={showTooltip}
      onMouseLeave={() => setIsVisible(false)}
      ref={triggerRef}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`tooltip-content tooltip-${position}`}
          style={tooltipStyle}
        >
          {content}
          <div className={`tooltip-arrow tooltip-arrow-${position}`}></div>
        </div>
      )}
    </div>
  );
};

// Payment Options Modal Component
const PaymentOptionsModal = ({ planGroup, isOpen, onClose, onSelectPlan }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !planGroup) return null;

  const handleSelectPayment = async (plan) => {
    console.log('Payment gateway clicked for plan:', {
      plan: plan,
      paymentGatewayId: plan.payment_gateway_id
    });

    setIsProcessing(true);
    setSelectedOption(`${plan.id}-${plan.payment_gateway_id}`);

    try {
      // Check for Stripe gateway (payment_gateway_id = 1 is typically Stripe)
      if (plan.payment_gateway_id === 1 || plan.payment_gateway_id === '1') {
        console.log('Stripe gateway detected, calling onSelectPlan...');
        await onSelectPlan(plan);
      } else {
        console.log('Other payment gateway ID:', plan.payment_gateway_id);
        // Handle other payment gateways here based on payment_gateway_id
        // You can add more conditions here for different gateways
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
    } finally {
      setIsProcessing(false);
      setSelectedOption(null);
    }
  };



  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>{planGroup.name}</h2>
          <p>Choose your billing cycle</p>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="payment-list">
          {planGroup.plans.map((plan) => {

            // Helper function to get gateway info based on payment_gateway_id
            const getGatewayInfo = (paymentGatewayId) => {
              switch (parseInt(paymentGatewayId)) {
                case 1:
                  return {
                    name: 'Stripe',
                    description: 'Cards, Apple Pay, Google Pay',
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.976 9.15c-2.172-.806-3.596-1.426-3.596-2.94 0-1.378 1.158-2.268 2.97-2.268 1.894 0 4.045.626 5.738 1.706l1.058-6.79C18.27.51 16.165 0 13.976 0 9.474 0 6.236 2.524 6.236 6.226c0 3.452 2.553 4.902 5.666 6.037 2.488.891 3.267 1.564 3.267 2.732 0 1.513-1.289 2.268-3.141 2.268-2.042 0-4.694-.864-6.535-2.268l-1.158 6.79C5.775 22.61 8.769 23.5 12.552 23.5c4.694 0 8.013-2.268 8.013-6.09 0-3.566-2.808-5.036-6.589-6.26z" fill="#635bff" />
                      </svg>
                    )
                  };
                case 2:
                  return {
                    name: 'PayPal',
                    description: 'PayPal, Credit Cards',
                    icon: '💳'
                  };
                case 3:
                  return {
                    name: 'Razorpay',
                    description: 'UPI, Cards, Net Banking',
                    icon: '💳'
                  };
                default:
                  return {
                    name: 'Payment Gateway',
                    description: 'Multiple Payment Options',
                    icon: '💳'
                  };
              }
            };

            const gatewayInfo = getGatewayInfo(plan.payment_gateway_id);

            // Debug: Log plan data structure to understand available fields
            console.log('Plan data for payment gateways:', {
              planId: plan.id,
              planData: plan,
              planGroupData: planGroup,
              paymentGatewayId: plan.payment_gateway_id,
              gatewayInfo: gatewayInfo
            });

            return (
              <div
                key={plan.id}
                className="payment-plan-card"
              >
                <div className="plan-header-info">
                  <div className="plan-title-price">
                    <h4>{plan.duration_type}</h4>
                    <div className="plan-price-display">
                      <span className="price-amount">${plan.price}</span>
                      <span className="price-period">/{plan.duration_type}</span>
                    </div>
                  </div>
                </div>

                <div className="plan-benefits">
                  <h5>What's included:</h5>
                  <ul>
                    <li>✓ {plan.upload_limit || planGroup.upload_limit} Ad Uploads per {plan.duration_type}</li>
                    <li>✓ {(plan.views_limit || planGroup.views_limit)?.toLocaleString()} Ad Views per {plan.duration_type}</li>
                    <li>✓ Premium Support & Analytics</li>
                    <li>✓ Multi-device Access</li>
                  </ul>
                </div>

                <div className="payment-gateways">
                  <h5>Choose Payment Method:</h5>
                  <div className="gateway-options">
                    <button
                      className={`gateway-btn ${selectedOption === `${plan.id}-${plan.payment_gateway_id}` ? 'processing' : ''}`}
                      onClick={() => handleSelectPayment(plan)}
                      disabled={isProcessing}
                    >
                      <div className="gateway-icon">
                        <span className="gateway-emoji-icon">
                          {gatewayInfo.icon}
                        </span>
                      </div>
                      <div className="gateway-info">
                        <span className="gateway-name">{gatewayInfo.name}</span>
                        <span className="gateway-desc">{gatewayInfo.description}</span>
                      </div>
                      {selectedOption === `${plan.id}-${plan.payment_gateway_id}` ? (
                        <div className="gateway-processing">
                          <span className="spinner-small"></span>
                        </div>
                      ) : (
                        <span className="gateway-arrow">→</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Feature Comparison Modal Component
const ComparisonModal = ({ plans, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="comparison-modal-overlay" onClick={onClose}>
      <div className="comparison-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="comparison-modal-header">
          <h2>Plan Comparison</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="comparison-table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Features</th>
                {plans.map(plan => (
                  <th key={plan.id}>{plan.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Price</td>
                {plans.map(plan => (
                  <td key={plan.id}>
                    <strong>${plan.price}/{plan.duration_type}</strong>
                  </td>
                ))}
              </tr>
              <tr>
                <td>Upload Limit</td>
                {plans.map(plan => (
                  <td key={plan.id}>{plan.upload_limit || 0} uploads</td>
                ))}
              </tr>
              <tr>
                <td>View Limit</td>
                {plans.map(plan => (
                  <td key={plan.id}>{plan.views_limit >= 1000 ? `${(plan.views_limit / 1000).toFixed(0)}K` : plan.views_limit || 0} views</td>
                ))}
              </tr>
              <tr>
                <td>Device Support</td>
                {plans.map(plan => (
                  <td key={plan.id}>{plan.devices?.length || 'All'} devices</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdvertiserPlans = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPlanGroup, setSelectedPlanGroup] = useState(null);

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  };


  useEffect(() => {
    fetchPlans();
    
    // Handle payment error redirects from backend
    const error = searchParams.get('error');
    const cancelled = searchParams.get('cancelled');
    
    if (error) {
      let errorMessage = 'Payment failed. Please try again.';
      switch (error) {
        case 'payment_failed':
          errorMessage = 'Payment was not completed successfully.';
          break;
        case 'plan_not_found':
          errorMessage = 'The selected plan could not be found.';
          break;
        case 'advertiser_not_found':
          errorMessage = 'Your account could not be verified.';
          break;
        case 'processing_failed':
          errorMessage = 'There was an error processing your payment.';
          break;
        case 'missing_parameters':
          errorMessage = 'Invalid payment session.';
          break;
        default:
          errorMessage = 'Payment failed. Please try again.';
      }
      toast.error(errorMessage);
      // Clear URL parameters
      setSearchParams({});
      
    } else if (cancelled === 'true') {
      toast.info('Payment was cancelled. You can try again anytime.');
      // Clear URL parameters
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the advertiser-specific API endpoint for getting advertisement plans
      const res = await axios.get(
        `${process.env.REACT_APP_Baseurl}/advertiser/advertisement/plans`,
        { headers }
      );

      if (res.data.status) {
        const plansData = res.data.data || [];
        console.log('Fetched plans:', plansData); // Debug log
        setPlans(plansData);
      } else {
        setError(res.data.message || 'Failed to load plans');
        toast.error(res.data.message || 'Failed to load plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load plans. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlanGroup = (planGroup) => {
    setSelectedPlanGroup(planGroup);
    setShowPaymentOptions(true);
  };

  const handleSelectPlan = async (plan) => {

    try {
      setSelectedPlan(plan.id);
      console.log('Set selected plan to:', plan.id);

      // Create Stripe checkout session
      const apiUrl = `${process.env.REACT_APP_Baseurl}/advertiser/stripe/create-checkout-session`;

      const response = await axios.post(apiUrl, { plan_id: plan.id }, { headers });

      if (response.data.status) {
        toast.success('Redirecting to payment...');

        // Close the payment options modal
        setShowPaymentOptions(false);
        setSelectedPlanGroup(null);

        window.location.href = response.data.checkout_url;
      } else {
        console.error('API returned error status:', response.data);
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      console.error('Full error object:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process payment. Please try again.';
      toast.error(errorMessage);
    } finally {
      console.log('Clearing selected plan');
      setSelectedPlan(null);
    }
  };

  const handleClosePaymentOptions = () => {
    setShowPaymentOptions(false);
    setSelectedPlanGroup(null);
  };

  const handleSkipForNow = () => {
    // Redirect to ads dashboard
    navigate('/ads-list');
  };


  const getPlanBadge = (plan) => {
    if (plan.price === 0) return 'Free';
    if (plan.views_limit > 10000) return 'Popular';
    return null;
  };

  const getMostPopularPlan = () => {
    if (plans.length === 0) return null;
    // Find the plan with highest views limit or middle-priced plan
    const sortedByPrice = [...plans].sort((a, b) => a.price - b.price);
    return sortedByPrice.length >= 3 ? sortedByPrice[1] : sortedByPrice[0];
  };

  const mostPopularPlan = getMostPopularPlan();

  // Group plans by group_plan_id
  const groupPlansByGroupId = (plans) => {
    const grouped = {};

    plans.forEach(plan => {
      const groupId = plan.group_plan_id || `single_${plan.id}`;

      if (!grouped[groupId]) {
        grouped[groupId] = {
          id: groupId,
          name: plan.name || 'Advertisement Plan',
          description: plan.description,
          upload_limit: plan.upload_limit,
          views_limit: plan.views_limit,
          features: plan.features,
          devices: plan.devices,
          plans: []
        };
      }

      grouped[groupId].plans.push(plan);
    });

    // Sort plans within each group by price
    Object.values(grouped).forEach(group => {
      group.plans.sort((a, b) => a.price - b.price);
      // Set the base price as the cheapest option
      group.basePrice = group.plans[0]?.price || 0;
      group.priceRange = group.plans.length > 1 ?
        `$${group.plans[0].price} - $${group.plans[group.plans.length - 1].price}` :
        `$${group.plans[0]?.price || 0}`;
    });

    return Object.values(grouped);
  };

  const getGroupedPlans = () => {
    return groupPlansByGroupId(plans);
  };

  // Search function for grouped plans
  const filterPlanGroups = (planGroups) => {
    let filtered = planGroups;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(group =>
        group.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const getDisplayPlanGroups = () => {
    const groupedPlans = getGroupedPlans();
    return filterPlanGroups(groupedPlans);
  };

  const handlePlanHover = (planId, isEntering) => {
    if (isEntering) {
      setHoveredPlan(planId);
    } else {
      setHoveredPlan(null);
    }
  };


  if (loading) {
    return (
      <div className="plans-container">
        <div className="plans-loading">
          <div className="spinner"></div>
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="plans-container">
        <div className="plans-error">
          <h2>Error Loading Plans</h2>
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/ads-list')}
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const displayPlanGroups = getDisplayPlanGroups();
  const groupedPlans = getGroupedPlans();

  return (
    <div className="plans-container">
      {/* Enhanced Header with Search and Controls */}
      <div className="plans-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Choose Your Advertising Plan</h1>
            <div className="header-features">
              <div className="feature-highlight">
                <span>Instant Setup</span>
              </div>
              <div className="feature-highlight">
                <span>Premium Support</span>
              </div>
              <div className="feature-highlight">
                <span>Fast Performance</span>
              </div>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <span className="stat-number">{plans.length}</span>
              <span className="stat-label">Total Plans</span>
            </div>
            <div className="stat-card featured">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
          </div>
        </div>
        <div className="header-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M1200 0L0 0 598.97 114.72 1200 0z" fill="rgba(255,255,255,0.1)"></path>
          </svg>
        </div>
      </div>

      {/* Enhanced Search and Filter Controls */}
      <div className="plans-controls">
        <div className="controls-container">
          <div className="controls-left">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search plans, features, pricing..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              {searchQuery && displayPlanGroups.length === 0 && (
                <div className="search-suggestions">
                  <p>Try searching for:</p>
                  <div className="suggestion-tags">
                    <button onClick={() => setSearchQuery('basic')}>Basic</button>
                    <button onClick={() => setSearchQuery('premium')}>Premium</button>
                    <button onClick={() => setSearchQuery('upload')}>Upload</button>
                    <button onClick={() => setSearchQuery('views')}>Views</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="controls-right">

            <button
              className="compare-btn"
              onClick={() => setShowComparison(true)}
              disabled={plans.length < 2}
              data-tooltip="Compare all available plans"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="9" />
              </svg>
              <span className="btn-text">Compare Plans</span>
              <span className="btn-count">({plans.length})</span>
            </button>
          </div>
        </div>
      </div>


      {/* Results Summary */}
      {searchQuery ? (
        <div className="results-summary">
          <p>
            Showing <strong>{displayPlanGroups.length}</strong> of <strong>{groupedPlans.length}</strong> plan categories
            {searchQuery && <span> for "{searchQuery}"</span>}
          </p>
        </div>
      ) : null}

      {/* Plan Groups Grid */}
      <div className="plans-grid">
        {displayPlanGroups.length > 0 ? (
          displayPlanGroups.map((planGroup) => {
            const badge = planGroup.basePrice === 0 ? 'Free' :
              planGroup.views_limit > 10000 ? 'Popular' : null;
            const isHovered = hoveredPlan === planGroup.id;

            return (
              <article
                key={planGroup.id}
                className={`plan-card ${isHovered ? 'hovered' : ''} plan-group-card`}
                onMouseEnter={() => handlePlanHover(planGroup.id, true)}
                onMouseLeave={() => handlePlanHover(planGroup.id, false)}
                role="button"
                tabIndex={0}
                aria-label={`${planGroup.name} - Starting from ${planGroup.priceRange}`}
              >
                {badge && (
                  <div className={`plan-badge ${planGroup.basePrice === 0 ? 'free' : 'popular'
                    }`}>
                    {badge}
                  </div>
                )}

                {planGroup.plans.length > 1 && (
                  <div className="plan-badge multiple-options">
                    {planGroup.plans.length} Options
                  </div>
                )}

                <div className="plan-header">
                  <div className="plan-title-section">
                    <h3>{planGroup.name || 'Advertisement Plan'}</h3>
                    <Tooltip content="Click to see payment options for this plan" position="top">
                      <button
                        className="info-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        aria-label="More information about this plan"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="12" cy="12" r="10" />
                          <path d="9,9 a3,4 0 1,1 6,0c0,2.3 -3,4 -3,6" />
                          <path d="12,17 h.01" />
                        </svg>
                      </button>
                    </Tooltip>
                  </div>

                  <div className="plan-price">
                    <Tooltip content={`Multiple billing options available`} position="bottom">
                      <div className="price-display">
                        <span className="amount">Starting at</span>
                        <span className="price-range">{planGroup.priceRange}</span>
                      </div>
                    </Tooltip>
                  </div>

                  {planGroup.description && (
                    <p className="plan-description">
                      {planGroup.description.length > 100 ?
                        `${planGroup.description.substring(0, 100)}...` :
                        planGroup.description
                      }
                    </p>
                  )}
                </div>

                <div className="plan-stats">
                  <Tooltip content={`Upload up to ${planGroup.upload_limit || 0} advertisements`} position="top">
                    <div className="stat-item">
                      <span className="stat-number">{planGroup.upload_limit || 0}</span>
                      <span className="stat-label">Ad Uploads</span>
                    </div>
                  </Tooltip>

                  <Tooltip content={`Your ads can receive up to ${planGroup.views_limit?.toLocaleString() || 0} views`} position="top">
                    <div className="stat-item">
                      <span className="stat-number">
                        {planGroup.views_limit ? (planGroup.views_limit >= 1000 ? `${(planGroup.views_limit / 1000).toFixed(0)}K` : planGroup.views_limit.toLocaleString()) : 0}
                      </span>
                      <span className="stat-label">Ad Views</span>
                    </div>
                  </Tooltip>

                  {planGroup.devices && planGroup.devices.length > 0 && (
                    <Tooltip content={`Compatible with ${planGroup.devices.length} device types`} position="top">
                      <div className="stat-item">
                        <span className="stat-number">{planGroup.devices.length}</span>
                        <span className="stat-label">Devices</span>
                      </div>
                    </Tooltip>
                  )}
                </div>

                <div className="plan-billing-options">
                  <div className="billing-preview">
                    <span className="billing-info">{planGroup.plans.map(plan => plan.duration_type.toUpperCase()).join(', ')}</span>
                  </div>
                </div>

                <div className="plan-actions">
                  <button
                    className="btn btn-primary plan-select-btn"
                    onClick={() => handleSelectPlanGroup(planGroup)}
                  >
                    Choose Payment Plan
                  </button>
                </div>
              </article>
            );
          })
        ) : (
          <div className="no-plans">
            <div className="no-plans-content">
              <div className="no-plans-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3>No Plans Found</h3>
              <p>
                {searchQuery
                  ? 'No plan categories match your current search criteria. Try adjusting your search terms.'
                  : 'There are currently no advertising plan categories available. Please check back later.'
                }
              </p>
              {searchQuery && (
                <button
                  className="btn btn-outline clear-filters-btn"
                  onClick={() => {
                    setSearchQuery('');
                  }}
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Footer Actions */}
      <div className="plans-actions">
        <div className="actions-content">
          <div className="actions-left">
            <p className="help-text">
              Need help choosing?
              <button
                className="link-btn"
                onClick={() => setShowComparison(true)}
                disabled={plans.length < 2}
              >
                Compare all plans
              </button>
            </p>
          </div>
          <div className="actions-right">
            <button
              className="btn btn-secondary skip-btn"
              onClick={handleSkipForNow}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12,5 19,12 12,19" />
              </svg>
              Skip for Now
            </button>
          </div>
        </div>
      </div>

      {/* Payment Options Modal */}
      {showPaymentOptions && (
        <PaymentOptionsModal
          planGroup={selectedPlanGroup}
          isOpen={showPaymentOptions}
          onClose={handleClosePaymentOptions}
          onSelectPlan={handleSelectPlan}
        />
      )}

      {/* Comparison Modal */}
      {showComparison && (
        <ComparisonModal
          plans={plans}
          isOpen={showComparison}
          onClose={() => setShowComparison(false)}
        />
      )}

      {/* Loading Overlay for Better UX */}
      {selectedPlan && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Processing your selection...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertiserPlans;